// import { RequestOptions, URLSearchParams } from '@angular/http';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { Observable, from, of, throwError } from 'rxjs';
import { Inject, Injectable, InjectionToken } from '@angular/core';

import { Plex } from '@andes/plex';
import { Options } from './options';
import { finalize, map, catchError, switchMap } from 'rxjs/operators';

// Constantes
const defaultOptions: Options = { params: null, showError: true, showLoader: true };

export const APP_HOST = new InjectionToken<boolean>('APP_HOST');

@Injectable()
export class Server {
    private baseURL: string;

    constructor(
        private http: HttpClient,
        private plex: Plex,
        @Inject(APP_HOST) private host: string
    ) {
        this.baseURL = this.host;
    }

    private parse(data: any): any {
        const dateISO = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:[.,]\d+)?Z/i;
        const dateNet = /\/Date\((-?\d+)(?:-\d+)?\)\//i;
        const traverse = function (o, func) {
            for (const i of Object.keys(o)) {
                o[i] = func.apply(this, [i, o[i]]);
                if (o[i] !== null && typeof (o[i]) === 'object') {
                    traverse(o[i], func);
                }
            }
        };
        const replacer = function (key, value) {
            if (typeof (value) === 'string') {
                if (dateISO.test(value)) {
                    return new Date(value);
                }
                if (dateNet.test(value)) {
                    return new Date(parseInt(dateNet.exec(value)[1], 10));
                }
            }
            return value;
        };
        if (typeof data === 'object') {
            traverse(data, replacer);
        }
        return data;

    }

    private stringify(object: any) {
        return JSON.stringify(object);
    }

    private prepareOptions(options: Options) {
        let token = window.sessionStorage.getItem('jwt') || options?.token || options?.params?.token;
        token = token ? `JWT ${token}` : '';
        const result: any = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': token
            })
        };
        if (options && options.responseType) {
            result.responseType = options.responseType;
        }
        if (options && options.params) {
            result.params = new HttpParams();
            for (const param in options.params) {
                if (options.params[param] !== undefined && options.params[param] !== null) {
                    if (Array.isArray(options.params[param])) {
                        (options.params[param] as Array<any>).forEach((value) => {
                            result.params = result.params.append(param, value);
                        });
                    } else {
                        if (options.params[param] instanceof Date) {
                            result.params = result.params.set(param, (options.params[param] as Date).toISOString());
                        } else {
                            result.params = result.params.set(param, options.params[param]);
                        }
                    }
                }
            }
        }
        return result;
    }

    private handleError(response: any, options: Options): Observable<never> {
        const body = response.error;

        // Las peticiones con responseType 'blob' también reciben los
        // errores HTTP como Blob, aunque el servidor devuelva JSON o texto.
        if (body instanceof Blob) {
            return from(body.text()).pipe(
                // Si el Blob no puede leerse, utilizamos el mensaje genérico.
                catchError(() => of('')),
                switchMap((text: string) => {
                    let error: any = text;

                    try {
                        error = JSON.parse(text);
                    } catch {
                        // La respuesta no contiene JSON; conservamos el texto.
                    }

                    return this.showHttpError(response, options, error);
                })
            );
        }

        return this.showHttpError(response, options, body);
    }

    private showHttpError(response: any, options: Options, body: any): Observable<never> {
        let message: string;

        if (body && typeof body.message === 'string') {
            message = body.message;
        } else if (typeof body === 'string' && body.trim() && !/<[a-z][\s\S]*>/i.test(body)) {
            message = body;
        } else {
            message = 'La aplicación no pudo comunicarse con el servidor. Por favor revise su conexión a la red.';
        }

        if (!options || options.showError || (options.showError === undefined)) {
            // El código 400 es usado para enviar mensaje de validación al usuario
            if (response.status === 400) {
                this.plex.info('warning', `<div class="text-muted small pt-3">Código de error: ${response.status}</div>`, message);
            } else {
                this.plex.info('danger', `${message}<div class="text-muted small pt-3">Código de error: ${response.status}</div>`, 'No se pudo conectar con el servidor');
            }
        }

        return throwError(() => message);
    }

    private getAbsoluteURL(url: string) {
        if (url.toLowerCase().startsWith('http')) {
            return url;
        } else {
            return this.baseURL + url;
        }
    }

    setBaseURL(baseURL: string) {
        this.baseURL = baseURL;
    }

    get(url: string, options: Options = defaultOptions): Observable<any> {
        return this.http.get(this.getAbsoluteURL(url), this.prepareOptions(options)).pipe(
            map((res: any) => this.parse(res)),
            catchError((err: any) => this.handleError(err, options))
        );
    }

    post(url: string, body: any, options: Options = null): Observable<any> {
        return this.http.post(this.getAbsoluteURL(url), this.stringify(body), this.prepareOptions(options)).pipe(
            map((res: any) => this.parse(res)),
            catchError((err: any) => this.handleError(err, options))
        );
    }

    put(url: string, body: any, options: Options = defaultOptions): Observable<any> {
        return this.http.put(this.getAbsoluteURL(url), this.stringify(body), this.prepareOptions(options)).pipe(
            map((res: any) => this.parse(res)),
            catchError((err: any) => this.handleError(err, options))
        );
    }

    patch(url: string, body: any, options: Options = defaultOptions): Observable<any> {
        return this.http.patch(this.getAbsoluteURL(url), this.stringify(body), this.prepareOptions(options)).pipe(
            map((res: any) => this.parse(res)),
            catchError((err: any) => this.handleError(err, options))
        );
    }

    delete(url: string, options: Options = defaultOptions): Observable<any> {
        return this.http.delete(this.getAbsoluteURL(url), this.prepareOptions(options)).pipe(
            map((res: any) => this.parse(res)),
            catchError((err: any) => this.handleError(err, options))
        );
    }
}
